import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    BanknotesIcon,
    BuildingOfficeIcon,
    ScaleIcon,
    CurrencyDollarIcon,
    ReceiptRefundIcon,
    DocumentChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import { formatCurrency } from '../../../utils/formatting';
import toast from 'react-hot-toast';
import AddAccountModal from '../modals/AddAccountModal';
import EditAccountModal from '../modals/EditAccountModal';
import AccountAnalysisModal from '../modals/AccountAnalysisModal';
import TrialBalanceModal from '../modals/TrialBalanceModal';

const ChartOfAccountsTab = () => {
    const { t, i18n } = useTranslation();
    const { currentCompany, currentUser } = useAuth();

    // State management
    const [loading, setLoading] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [accountHierarchy, setAccountHierarchy] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list'); // Default to list view
    const [filterType, setFilterType] = useState('all');
    const [expandedGroups, setExpandedGroups] = useState({
        asset: true,
        liability: true,
        equity: true,
        revenue: true,
        expense: true
    });

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [showTrialBalanceModal, setShowTrialBalanceModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);

    // Statistics
    const [stats, setStats] = useState({
        totalAccounts: 0,
        activeAccounts: 0,
        totalAssets: 0,
        totalLiabilities: 0,
        totalEquity: 0,
        totalRevenue: 0,
        totalExpenses: 0
    });

    const loadAccounts = useCallback(async () => {
        if (!currentCompany?.id) return;

        setLoading(true);
        try {
            let accountsData = await chartOfAccountsService.getChartOfAccounts(currentCompany.id, true);
            
            // 🚀 AUTOMATICALLY INITIALIZE DEFAULT ACCOUNTS IF NONE EXIST
            if (accountsData.length === 0) {
                console.log('🏢 No accounts found, automatically initializing default accounts...');
                try {
                    const initialized = await chartOfAccountsService.initializeDefaultChartOfAccounts(
                        currentCompany.id, 
                        currentUser?.uid || 'system'
                    );
                    
                    if (initialized) {
                        console.log('✅ Default accounts initialized successfully');
                        // Reload accounts after initialization
                        accountsData = await chartOfAccountsService.getChartOfAccounts(currentCompany.id, true);
                        toast.success(t('coa.success.auto_initialized', 'Default chart of accounts initialized automatically! 📊'));
                    }
                } catch (initError) {
                    console.error('❌ Failed to auto-initialize default accounts:', initError);
                    // Don't show error toast, just log it - user can still manually add accounts
                }
            }

            const hierarchyData = await chartOfAccountsService.getAccountHierarchy(currentCompany.id);
            
            setAccounts(accountsData);
            setAccountHierarchy(hierarchyData);

            const activeAccounts = accountsData.filter(acc => acc.isActive);
            setStats({
                totalAccounts: accountsData.length,
                activeAccounts: activeAccounts.length,
                totalAssets: hierarchyData.asset?.totalBalance || 0,
                totalLiabilities: hierarchyData.liability?.totalBalance || 0,
                totalEquity: hierarchyData.equity?.totalBalance || 0,
                totalRevenue: hierarchyData.revenue?.totalBalance || 0,
                totalExpenses: hierarchyData.expense?.totalBalance || 0
            });

        } catch (error) {
            console.error('Error loading chart of accounts:', error);
            toast.error(t('coa.error.loading_failed', 'Failed to load chart of accounts'));
        } finally {
            setLoading(false);
        }
    }, [currentCompany?.id, currentUser?.uid, t]);

    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    const handleAccountCreated = () => {
        setShowAddModal(false);
        loadAccounts();
        toast.success(t('coa.success.account_created', 'Account created successfully'));
    };

    const handleAccountUpdated = () => {
        setShowEditModal(false);
        setSelectedAccount(null);
        loadAccounts();
        toast.success(t('coa.success.account_updated', 'Account updated successfully'));
    };

    const handleDeleteAccount = async (account) => {
        if (!window.confirm(t('coa.confirm.delete_account', 'Are you sure you want to delete this account?'))) {
            return;
        }

        try {
            await chartOfAccountsService.deleteAccount(account.id, currentUser?.uid);
            loadAccounts();
            toast.success(t('coa.success.account_deleted', 'Account deleted successfully'));
        } catch (error) {
            console.error('Error deleting account:', error);
            if (error.message.includes('used in transactions')) {
                toast.error(t('coa.error.account_in_use', 'Cannot delete account that has been used in transactions'));
            } else {
                toast.error(t('coa.error.delete_failed', 'Failed to delete account'));
            }
        }
    };

    const handleInitializeDefaults = async () => {
        if (!window.confirm(t('coa.confirm.initialize_defaults', 'This will create a comprehensive chart of accounts for your logistics business. Continue?'))) {
            return;
        }

        setLoading(true);
        try {
            console.log('🚀 Initializing default chart of accounts...');
            const result = await chartOfAccountsService.initializeDefaultChartOfAccounts(
                currentCompany.id, 
                currentUser?.uid || 'system'
            );

            if (result) {
                await loadAccounts();
                toast.success(t('coa.success.defaults_initialized', 'Default chart of accounts initialized successfully! 📊'));
            } else {
                toast.error(t('coa.error.defaults_exist', 'Default accounts already exist or initialization failed'));
            }
        } catch (error) {
            console.error('Error initializing default accounts:', error);
            if (error.message.includes('already exist')) {
                toast.error(t('coa.error.defaults_exist', 'Default accounts already exist'));
            } else {
                toast.error(t('coa.error.initialization_failed', 'Failed to initialize default accounts'));
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
                <span className={`${i18n.language === 'ar' ? 'mr-3' : 'ml-3'} text-gray-600`}>{t('common.loading', t('common.loading'))}</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="card">
                    <div className="card-body text-center">
                        <BanknotesIcon className="w-8 h-8 mx-auto text-green-600 mb-2" />
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalAssets)}</div>
                        <div className="text-sm text-gray-500">{t('coa.total_assets', 'Total Assets')}</div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body text-center">
                        <BuildingOfficeIcon className="w-8 h-8 mx-auto text-red-600 mb-2" />
                        <div className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalLiabilities)}</div>
                        <div className="text-sm text-gray-500">{t('coa.total_liabilities', 'Total Liabilities')}</div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body text-center">
                        <ScaleIcon className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                        <div className="text-2xl font-bold text-blue-600">{formatCurrency(stats.totalEquity)}</div>
                        <div className="text-sm text-gray-500">{t('coa.total_equity', 'Total Equity')}</div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body text-center">
                        <CurrencyDollarIcon className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                        <div className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalRevenue)}</div>
                        <div className="text-sm text-gray-500">{t('coa.total_revenue', 'Total Revenue')}</div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body text-center">
                        <ReceiptRefundIcon className="w-8 h-8 mx-auto text-orange-600 mb-2" />
                        <div className="text-2xl font-bold text-orange-600">{formatCurrency(stats.totalExpenses)}</div>
                        <div className="text-sm text-gray-500">{t('coa.total_expenses', 'Total Expenses')}</div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">{t('coa.chart_of_accounts', 'Chart of Accounts')}</h2>
                    <p className="text-sm text-gray-500">
                        {stats.activeAccounts} {t('coa.active_of', 'active of')} {stats.totalAccounts} {t('coa.total_accounts', 'total accounts')}
                    </p>
                </div>

                <div className="flex items-center space-x-2 mt-4 sm:mt-0">
                    <button
                        onClick={() => setShowTrialBalanceModal(true)}
                        className="btn btn-outline"
                    >
                        <DocumentChartBarIcon className={`w-4 h-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                        {t('coa.trial_balance', 'Trial Balance')}
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="btn btn-primary"
                    >
                        <PlusIcon className={`w-4 h-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                        {t('coa.add_account', 'Add Account')}
                    </button>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className={`w-5 h-5 absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400`} />
                        <input
                            type="text"
                            placeholder={t('coa.search_accounts', 'Search accounts...')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`${i18n.language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    <div className="relative">
                        <FunnelIcon className={`w-5 h-5 absolute ${i18n.language === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400`} />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className={`${i18n.language === 'ar' ? 'pr-10 pl-8' : 'pl-10 pr-8'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white`}
                        >
                            <option value="all">{t('coa.all_types', 'All Types')}</option>
                            <option value="asset">{t('coa.account_types.asset', 'Assets')}</option>
                            <option value="liability">{t('coa.account_types.liability', 'Liabilities')}</option>
                            <option value="equity">{t('coa.account_types.equity', 'Equity')}</option>
                            <option value="revenue">{t('coa.account_types.revenue', 'Revenue')}</option>
                            <option value="expense">{t('coa.account_types.expense', 'Expenses')}</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => setViewMode('tree')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'tree'
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {t('coa.tree_view', 'Tree View')}
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list'
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {t('coa.list_view', 'List View')}
                    </button>
                </div>
            </div>

            {/* Accounts Display */}
            <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                    {accounts.length === 0 ? (
                        <div className="text-center py-12">
                            <BanknotesIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {t('coa.no_accounts', 'No Accounts Found')}
                            </h3>
                            <p className="text-gray-500 mb-4">
                                {t('coa.no_accounts_description', 'Get started by creating your first account or initializing default accounts.')}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={handleInitializeDefaults}
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <LoadingSpinner size="sm" />
                                            {t('coa.initializing', 'Initializing...')}
                                        </>
                                    ) : (
                                        <>
                                            <DocumentChartBarIcon className={`w-4 h-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                                            {t('coa.initialize_defaults', 'Initialize Default Accounts')}
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="btn btn-outline"
                                >
                                    <PlusIcon className={`w-4 h-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                                    {t('coa.create_first_account', 'Create First Account')}
                                </button>
                            </div>
                        </div>
                    ) : viewMode === 'tree' ? (
                        <div className="space-y-4" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                            {Object.entries(accountHierarchy).map(([type, group]) => {
                                const isExpanded = expandedGroups[type];
                                const filteredAccounts = group.accounts?.filter(account => {
                                    if (searchTerm) {
                                        const term = searchTerm.toLowerCase();
                                        return account.accountCode.includes(term) ||
                                            account.accountName.toLowerCase().includes(term) ||
                                            (account.accountNameAr && account.accountNameAr.includes(term));
                                    }
                                    return true;
                                }) || [];

                                if (filteredAccounts.length === 0 && searchTerm) return null;

                                const getAccountTypeIcon = (accountType) => {
                                    const icons = {
                                        asset: BanknotesIcon,
                                        liability: BuildingOfficeIcon,
                                        equity: ScaleIcon,
                                        revenue: CurrencyDollarIcon,
                                        expense: ReceiptRefundIcon
                                    };
                                    return icons[accountType] || InformationCircleIcon;
                                };

                                const getAccountTypeColor = (accountType) => {
                                    const colors = {
                                        asset: 'text-green-600',
                                        liability: 'text-red-600',
                                        equity: 'text-blue-600',
                                        revenue: 'text-purple-600',
                                        expense: 'text-orange-600'
                                    };
                                    return colors[accountType] || 'text-gray-600';
                                };

                                const Icon = getAccountTypeIcon(type);

                                return (
                                    <div key={type} className="border border-gray-200 rounded-lg">
                                        {/* Group Header */}
                                        <div
                                            className={`flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors`}
                                            onClick={() => setExpandedGroups(prev => ({
                                                ...prev,
                                                [type]: !prev[type]
                                            }))}
                                        >
                                            <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                                {isExpanded ? (
                                                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                                ) : (
                                                    i18n.language === 'ar' ? (
                                                        <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
                                                    ) : (
                                                        <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                                                    )
                                                )}
                                                <Icon className={`w-5 h-5 ${getAccountTypeColor(type)}`} />
                                                <div>
                                                    <h3 className="font-medium text-gray-900 text-sm text-start">
                                                        {t(`coa.account_types.${type}`, group.name)}
                                                    </h3>
                                                    <p className="text-xs text-gray-500 text-start">
                                                        {filteredAccounts.length} {t('coa.accounts', 'accounts')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className="font-medium text-gray-900 text-sm">
                                                    {formatCurrency(group.totalBalance || 0)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {t('coa.total_balance', 'Total Balance')}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Group Accounts */}
                                        {isExpanded && (
                                            <div className="divide-y divide-gray-200">
                                                {filteredAccounts.map(account => (
                                                    <div key={account.id} className="p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                                                        onClick={() => {
                                                            setSelectedAccount(account);
                                                            setShowAnalysisModal(true);
                                                        }}>
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                                                    <span className="font-mono text-sm font-medium text-gray-900">
                                                                        {account.accountCode}
                                                                    </span>
                                                                    <span className="font-medium text-gray-900 text-sm">
                                                                        {i18n.language === 'ar' && account.accountNameAr
                                                                            ? account.accountNameAr
                                                                            : account.accountName}
                                                                    </span>
                                                                    {!account.isActive && (
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                            {t('coa.inactive', t('common.inactive'))}
                                                                        </span>
                                                                    )}
                                                                    {account.isVatAccount && (
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                            VAT
                                                                        </span>
                                                                    )}
                                                                    {account.isZakatAccount && (
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                            {i18n.language === 'ar' ? 'زكاة' : 'Zakat'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                                                <div className="text-end">
                                                                    <div className="font-medium text-gray-900 text-sm">
                                                                        {formatCurrency(account.currentBalance || 0)}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {account.normalBalance === 'debit' ? t('coa.debit', 'Debit') : t('coa.credit', 'Credit')}
                                                                    </div>
                                                                </div>
                                                                <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedAccount(account);
                                                                            setShowAnalysisModal(true);
                                                                        }}
                                                                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                                        title={t('coa.view_analysis', 'View Analysis')}
                                                                    >
                                                                        <EyeIcon className="w-3 h-3" />
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedAccount(account);
                                                                            setShowEditModal(true);
                                                                        }}
                                                                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                                                                        title={t('coa.edit_account', 'Edit Account')}
                                                                    >
                                                                        <PencilIcon className="w-3 h-3" />
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteAccount(account);
                                                                        }}
                                                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                                        title={t('coa.delete_account', 'Delete Account')}
                                                                    >
                                                                        <TrashIcon className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // List View - COMPACT VERSION
                        <div className="overflow-x-auto" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-1.5 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('coa.account_code', 'Code')}
                                        </th>
                                        <th className="px-3 py-1.5 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('coa.account_name', t('common.name'))}
                                        </th>
                                        <th className="px-3 py-1.5 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('coa.account_type', 'Type')}
                                        </th>
                                        <th className="px-3 py-1.5 text-end text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('coa.current_balance', 'Balance')}
                                        </th>
                                        <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('coa.status', t('common.status'))}
                                        </th>
                                        <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('common.actions', t('common.actions'))}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {accounts.filter(account => {
                                        // Apply search filter
                                        if (searchTerm) {
                                            const term = searchTerm.toLowerCase();
                                            const matchesSearch = account.accountCode.includes(term) ||
                                                account.accountName.toLowerCase().includes(term) ||
                                                (account.accountNameAr && account.accountNameAr.includes(term));
                                            if (!matchesSearch) return false;
                                        }

                                        // Apply type filter
                                        if (filterType !== 'all' && account.accountType !== filterType) {
                                            return false;
                                        }

                                        return true;
                                    }).map(account => {
                                        const getAccountTypeIcon = (accountType) => {
                                            const icons = {
                                                asset: BanknotesIcon,
                                                liability: BuildingOfficeIcon,
                                                equity: ScaleIcon,
                                                revenue: CurrencyDollarIcon,
                                                expense: ReceiptRefundIcon
                                            };
                                            return icons[accountType] || InformationCircleIcon;
                                        };

                                        const getAccountTypeColor = (accountType) => {
                                            const colors = {
                                                asset: 'text-green-600',
                                                liability: 'text-red-600',
                                                equity: 'text-blue-600',
                                                revenue: 'text-purple-600',
                                                expense: 'text-orange-600'
                                            };
                                            return colors[accountType] || 'text-gray-600';
                                        };

                                        const Icon = getAccountTypeIcon(account.accountType);
                                        const displayName = i18n.language === 'ar' && account.accountNameAr
                                            ? account.accountNameAr
                                            : account.accountName;

                                        return (
                                            <tr key={account.id} className="hover:bg-gray-50 cursor-pointer"
                                                onClick={() => {
                                                    setSelectedAccount(account);
                                                    setShowAnalysisModal(true);
                                                }}>
                                                <td className="px-3 py-1.5 whitespace-nowrap">
                                                    <span className="font-mono text-xs font-medium text-gray-900">
                                                        {account.accountCode}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {displayName}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-1.5 whitespace-nowrap">
                                                    <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                                                        <Icon className={`w-3 h-3 ${getAccountTypeColor(account.accountType)}`} />
                                                        <span className="text-xs text-gray-700">
                                                            {t(`coa.account_types.${account.accountType}`, account.accountType)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-end">
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {formatCurrency(account.currentBalance || 0)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-center">
                                                    <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                                                        {account.isActive ? (
                                                            <CheckCircleIcon className="w-3 h-3 text-green-500" />
                                                        ) : (
                                                            <ExclamationTriangleIcon className="w-3 h-3 text-red-500" />
                                                        )}
                                                        {account.isVatAccount && (
                                                            <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                VAT
                                                            </span>
                                                        )}
                                                        {account.isZakatAccount && (
                                                            <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                {i18n.language === 'ar' ? 'زكاة' : 'Zakat'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-center">
                                                    <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-0.5' : 'space-x-0.5'}`}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedAccount(account);
                                                                setShowAnalysisModal(true);
                                                            }}
                                                            className="p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
                                                            title={t('coa.view_analysis', 'View Analysis')}
                                                        >
                                                            <EyeIcon className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedAccount(account);
                                                                setShowEditModal(true);
                                                            }}
                                                            className="p-0.5 text-gray-400 hover:text-green-600 transition-colors"
                                                            title={t('coa.edit_account', 'Edit Account')}
                                                        >
                                                            <PencilIcon className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteAccount(account);
                                                            }}
                                                            className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                                                            title={t('coa.delete_account', 'Delete Account')}
                                                        >
                                                            <TrashIcon className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <AddAccountModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleAccountCreated}
            />

            <EditAccountModal
                isOpen={showEditModal}
                account={selectedAccount}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedAccount(null);
                }}
                onSuccess={handleAccountUpdated}
            />

            <AccountAnalysisModal
                isOpen={showAnalysisModal}
                account={selectedAccount}
                onClose={() => {
                    setShowAnalysisModal(false);
                    setSelectedAccount(null);
                }}
            />

            <TrialBalanceModal
                isOpen={showTrialBalanceModal}
                onClose={() => setShowTrialBalanceModal(false)}
            />
        </div>
    );
};

export default ChartOfAccountsTab;